import { useEffect, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { Welcome } from './components/Welcome';
import { Composer } from './components/Composer';
import { RitualOutput } from './components/RitualOutput';
import { ReturnLink } from './components/ReturnLink';
import { composeRitual } from './lib/compose';
import type { Ritual, SavedRitual } from './data/ingredients';
import { DEFAULT_RITUAL } from './data/ingredients';
import styles from './App.module.css';

type Phase = 'welcome' | 'composer' | 'output';
const SAVED_KEY = 'ritual-forge:rituals';

function newId() {
  return `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export function App() {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [ritual, setRitual] = useState<Ritual>(DEFAULT_RITUAL);
  const [saved, setSaved] = useState<SavedRitual[]>(() =>
    storage.get<SavedRitual[]>(SAVED_KEY, [])
  );
  const [currentSavedId, setCurrentSavedId] = useState<string | null>(null);

  useEffect(() => {
    storage.set(SAVED_KEY, saved);
  }, [saved]);

  const isSaved = currentSavedId !== null && saved.some((r) => r.id === currentSavedId);

  function begin() {
    setRitual(DEFAULT_RITUAL);
    setCurrentSavedId(null);
    setPhase('composer');
  }

  function loadRitual(id: string) {
    const found = saved.find((r) => r.id === id);
    if (!found) return;
    setRitual(found.ritual);
    setCurrentSavedId(found.id);
    setPhase('output');
  }

  function deleteRitual(id: string) {
    setSaved((s) => s.filter((r) => r.id !== id));
    if (currentSavedId === id) {
      setCurrentSavedId(null);
      setPhase('welcome');
    }
  }

  function saveCurrent() {
    const id = currentSavedId ?? newId();
    const record: SavedRitual = {
      id,
      ritual,
      composed: composeRitual(ritual),
      createdAt: new Date().toISOString(),
    };
    setSaved((s) => {
      const idx = s.findIndex((r) => r.id === id);
      return idx >= 0 ? [...s.slice(0, idx), record, ...s.slice(idx + 1)] : [record, ...s];
    });
    setCurrentSavedId(id);
  }

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {phase !== 'welcome' && (
          <button
            type="button"
            className={styles.backHome}
            onClick={() => setPhase('welcome')}
          >
            ← my rituals
          </button>
        )}
      </header>

      <main className={styles.stage}>
        {phase === 'welcome' && (
          <Welcome
            saved={saved}
            onBegin={begin}
            onLoad={loadRitual}
            onDelete={deleteRitual}
          />
        )}
        {phase === 'composer' && (
          <Composer
            ritual={ritual}
            onChange={setRitual}
            onForge={() => setPhase('output')}
          />
        )}
        {phase === 'output' && (
          <RitualOutput
            ritual={ritual}
            saved={isSaved}
            onSave={saveCurrent}
            onEdit={() => setPhase('composer')}
            onAnother={begin}
          />
        )}
      </main>

      <footer className={styles.footer}>
        <p>composed rituals live in your browser. nothing is uploaded.</p>
      </footer>
    </>
  );
}
